import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Helmet } from "react-helmet-async";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

// Fallback for /emergency/service (hosting cannot serve HTTP 301/308):
// noindex + canonical to /emergency, plus client-side redirect.
const EmergencyServiceRedirect = () => (
  <>
    <Helmet>
      <link rel="canonical" href="https://tronelectrical.com/emergency" />
    </Helmet>
    <Navigate to="/emergency" replace />
  </>
);
import SiteHeader from "@/components/SiteHeader";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import StickyEstimateButton from "@/components/StickyEstimateButton";
import BackToTopButton from "@/components/BackToTopButton";
import HashScrollHandler from "@/components/HashScrollHandler";
import TrailingSlashRedirect from "@/components/TrailingSlashRedirect";
import ConversionTracking from "@/components/ConversionTracking";
import HeadDedupe from "@/components/HeadDedupe";
// Industrial Services
// Smart Home Services
// EV & Energy Services
// Emergency Services

// GTA Location Pages


// Toronto Neighborhood Pages

// Import new GTA city components



const Index = lazyWithRetry(() => import("./pages/Index"));
const About = lazyWithRetry(() => import("./pages/About"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const Careers = lazyWithRetry(() => import("./pages/Careers"));
const Emergency = lazyWithRetry(() => import("./pages/Emergency"));
const Commercial = lazyWithRetry(() => import("./pages/Commercial"));
const Warranty = lazyWithRetry(() => import("./pages/Warranty"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const PropertyManagement = lazyWithRetry(() => import("./pages/PropertyManagement"));
const IndustrialElectrician = lazyWithRetry(() => import("./pages/IndustrialElectrician"));
const Answers = lazyWithRetry(() => import("./pages/Answers"));
const ResidentialServicePage = lazyWithRetry(() => import("./pages/services/residential/ResidentialServicePage"));
const ElectricalPanelUpgrades = lazyWithRetry(() => import("./pages/services/residential/ElectricalPanelUpgrades"));
const FuseBoxBreakerConversions = lazyWithRetry(() => import("./pages/services/residential/FuseBoxBreakerConversions"));
const PotLightsLedLighting = lazyWithRetry(() => import("./pages/services/residential/PotLightsLedLighting"));
const CeilingBathroomFansGfci = lazyWithRetry(() => import("./pages/services/residential/CeilingBathroomFansGfci"));
const KnobTubeAluminumWiring = lazyWithRetry(() => import("./pages/services/residential/KnobTubeAluminumWiring"));
const SurgeProtectionSmokeAlarms = lazyWithRetry(() => import("./pages/services/residential/SurgeProtectionSmokeAlarms"));
const HotTubPoolAcConnections = lazyWithRetry(() => import("./pages/services/residential/HotTubPoolAcConnections"));
const LandscapeOutdoorLighting = lazyWithRetry(() => import("./pages/services/residential/LandscapeOutdoorLighting"));
const CameraSystems = lazyWithRetry(() => import("./pages/services/residential/CameraSystems"));
const MotionSecurityLighting = lazyWithRetry(() => import("./pages/services/residential/MotionSecurityLighting"));
const ElectricalRepairTroubleshooting = lazyWithRetry(() => import("./pages/services/residential/ElectricalRepairTroubleshooting"));
const ResidentialElectrician = lazyWithRetry(() => import("./pages/services/residential/ResidentialElectrician"));
const OfficeRetailRestaurantWiring = lazyWithRetry(() => import("./pages/services/commercial/OfficeRetailRestaurantWiring"));
const CommercialPanelLightingUpgrades = lazyWithRetry(() => import("./pages/services/commercial/CommercialPanelLightingUpgrades"));
const TransformersSubpanels = lazyWithRetry(() => import("./pages/services/commercial/TransformersSubpanels"));
const ExitSecurityLighting = lazyWithRetry(() => import("./pages/services/commercial/ExitSecurityLighting"));
const PowerDistributionMaintenance = lazyWithRetry(() => import("./pages/services/commercial/PowerDistributionMaintenance"));
const SixHundredVoltServices = lazyWithRetry(() => import("./pages/services/industrial/SixHundredVoltServices"));
const MachineryConnections = lazyWithRetry(() => import("./pages/services/industrial/MachineryConnections"));
const TransformerMotorControl = lazyWithRetry(() => import("./pages/services/industrial/TransformerMotorControl"));
const PlcAutomationWiring = lazyWithRetry(() => import("./pages/services/industrial/PlcAutomationWiring"));
const LutronHomeworks = lazyWithRetry(() => import("./pages/services/smart-home/LutronHomeworks"));
const RadioRa2 = lazyWithRetry(() => import("./pages/services/smart-home/RadioRa2"));
const RadioRa3 = lazyWithRetry(() => import("./pages/services/smart-home/RadioRa3"));
const PalladiomShades = lazyWithRetry(() => import("./pages/services/smart-home/PalladiomShades"));
const SmartPanelsOccupancy = lazyWithRetry(() => import("./pages/services/smart-home/SmartPanelsOccupancy"));
const VoiceControlIntegration = lazyWithRetry(() => import("./pages/services/smart-home/VoiceControlIntegration"));
const SmartThermostats = lazyWithRetry(() => import("./pages/services/smart-home/SmartThermostats"));
const EvChargerInstallation = lazyWithRetry(() => import("./pages/services/ev-energy/EvChargerInstallation"));
const GeneratorWiring = lazyWithRetry(() => import("./pages/services/ev-energy/GeneratorWiring"));
const EnergyManagement = lazyWithRetry(() => import("./pages/services/ev-energy/EnergyManagement"));
const EmergencyElectricalRepairs = lazyWithRetry(() => import("./pages/services/emergency/EmergencyElectricalRepairs"));
const PowerOutageResponse = lazyWithRetry(() => import("./pages/services/emergency/PowerOutageResponse"));
const BreakerTripsFixes = lazyWithRetry(() => import("./pages/services/emergency/BreakerTripsFixes"));
const EmergencyPanelRepairs = lazyWithRetry(() => import("./pages/services/emergency/EmergencyPanelRepairs"));
const ThankYou = lazyWithRetry(() => import("./pages/ThankYou"));
const AttributionReview = lazyWithRetry(() => import("./pages/AttributionReview"));
const Vaughan = lazyWithRetry(() => import("./pages/gta/Vaughan"));
const RichmondHill = lazyWithRetry(() => import("./pages/gta/RichmondHill"));
const Markham = lazyWithRetry(() => import("./pages/gta/Markham"));
const Mississauga = lazyWithRetry(() => import("./pages/gta/Mississauga"));
const Brampton = lazyWithRetry(() => import("./pages/gta/Brampton"));
const Oakville = lazyWithRetry(() => import("./pages/gta/Oakville"));
const NorthYorkGta = lazyWithRetry(() => import("./pages/gta/NorthYorkGta"));
const EtobicokeGta = lazyWithRetry(() => import("./pages/gta/EtobicokeGta"));
const ScarboroughGta = lazyWithRetry(() => import("./pages/gta/ScarboroughGta"));
const EastYorkGta = lazyWithRetry(() => import("./pages/gta/EastYorkGta"));
const Thornhill = lazyWithRetry(() => import("./pages/gta/Thornhill"));
const Woodbridge = lazyWithRetry(() => import("./pages/gta/Woodbridge"));
const Maple = lazyWithRetry(() => import("./pages/gta/Maple"));
const KingCity = lazyWithRetry(() => import("./pages/gta/KingCity"));
const Kleinburg = lazyWithRetry(() => import("./pages/gta/Kleinburg"));
const Bolton = lazyWithRetry(() => import("./pages/gta/Bolton"));
const Caledon = lazyWithRetry(() => import("./pages/gta/Caledon"));
const Georgetown = lazyWithRetry(() => import("./pages/gta/Georgetown"));
const DowntownToronto = lazyWithRetry(() => import("./pages/toronto/DowntownToronto"));
const MidtownToronto = lazyWithRetry(() => import("./pages/toronto/MidtownToronto"));
const TheBeaches = lazyWithRetry(() => import("./pages/toronto/TheBeaches"));
const Leslieville = lazyWithRetry(() => import("./pages/toronto/Leslieville"));
const HighPark = lazyWithRetry(() => import("./pages/toronto/HighPark"));
const JunctionTriangle = lazyWithRetry(() => import("./pages/toronto/JunctionTriangle"));
const ForestHill = lazyWithRetry(() => import("./pages/toronto/ForestHill"));
const Rosedale = lazyWithRetry(() => import("./pages/toronto/Rosedale"));
const Corktown = lazyWithRetry(() => import("./pages/toronto/Corktown"));
const LibertyVillage = lazyWithRetry(() => import("./pages/toronto/LibertyVillage"));
const KingWest = lazyWithRetry(() => import("./pages/toronto/KingWest"));
const Yorkville = lazyWithRetry(() => import("./pages/toronto/Yorkville"));
const Riverdale = lazyWithRetry(() => import("./pages/toronto/Riverdale"));
const TheDanforth = lazyWithRetry(() => import("./pages/toronto/TheDanforth"));
const BloorWest = lazyWithRetry(() => import("./pages/toronto/BloorWest"));
const LittleItaly = lazyWithRetry(() => import("./pages/toronto/LittleItaly"));
const TheAnnex = lazyWithRetry(() => import("./pages/toronto/TheAnnex"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const BlogCategory = lazyWithRetry(() => import("./pages/BlogCategory"));
const BlogTag = lazyWithRetry(() => import("./pages/BlogTag"));
const GTA = lazyWithRetry(() => import("./pages/GTA"));
const Ajax = lazyWithRetry(() => import("./pages/gta/Ajax"));
const Aurora = lazyWithRetry(() => import("./pages/gta/Aurora"));
const Burlington = lazyWithRetry(() => import("./pages/gta/Burlington"));
const Milton = lazyWithRetry(() => import("./pages/gta/Milton"));
const Newmarket = lazyWithRetry(() => import("./pages/gta/Newmarket"));
const Oshawa = lazyWithRetry(() => import("./pages/gta/Oshawa"));
const Pickering = lazyWithRetry(() => import("./pages/gta/Pickering"));
const Whitby = lazyWithRetry(() => import("./pages/gta/Whitby"));
const KensingtonMarket = lazyWithRetry(() => import("./pages/toronto/KensingtonMarket"));
const GuidePage = lazyWithRetry(() => import("./pages/GuidePage"));

// Everything inside the router. Rendered by BrowserRouter in the browser and by
// StaticRouter at build time (src/entry-server.tsx) to pre-render each page.
export const AppContent = () => (
  <>
        <TrailingSlashRedirect />
        <HashScrollHandler />
        <ConversionTracking />
        <HeadDedupe />
        <SiteHeader />
        <StickyMobileCTA />
        <StickyEstimateButton />
        <BackToTopButton />
        <Suspense fallback={<div className="min-h-[60vh]" aria-busy="true" />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/toronto" element={<Navigate to="/" replace />} />
          <Route path="/smart-home/radio-ra3" element={<Navigate to="/smart-home/radiora-3" replace />} />
          <Route path="/smart-home/radio-ra2" element={<Navigate to="/smart-home/radiora-2" replace />} />
          <Route path="/ev-energy" element={<Navigate to="/ev-energy/charger-installation" replace />} />
          <Route path="/ev-chargers" element={<Navigate to="/ev-energy/charger-installation" replace />} />
          <Route path="/home-automation" element={<Navigate to="/smart-home/lutron-homeworks" replace />} />
          <Route path="/residential/landscape-lighting" element={<Navigate to="/residential/landscape-outdoor-lighting" replace />} />
          <Route path="/services/industrial/machinery-connections" element={<Navigate to="/industrial-electrician" replace />} />
          <Route path="/leslieville" element={<Navigate to="/toronto/leslieville" replace />} />
          <Route path="/riverdale" element={<Navigate to="/toronto/riverdale" replace />} />
          <Route path="/bloor-west" element={<Navigate to="/toronto/bloor-west" replace />} />
          <Route path="/gta/bloor-west" element={<Navigate to="/toronto/bloor-west" replace />} />
          <Route path="/gta/junction-triangle" element={<Navigate to="/toronto/junction-triangle" replace />} />
          <Route path="/electrical-services" element={<Navigate to="/" replace />} />
          <Route path="/residential-services" element={<Navigate to="/" replace />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          
          {/* Emergency Page */}
          <Route path="/emergency" element={<Emergency />} />
          
          {/* Residential Service Pages */}
          <Route path="/residential/panel-upgrades" element={<ElectricalPanelUpgrades />} />
          <Route path="/residential/pot-lights" element={<Navigate to="/residential/pot-lights-led-lighting" replace />} />
          <Route path="/residential/heated-driveways" element={<ResidentialServicePage slug="heated-driveways" />} />
          <Route path="/residential/roof-de-icing" element={<ResidentialServicePage slug="roof-de-icing" />} />
          <Route path="/residential/radiant-floor-heating" element={<ResidentialServicePage slug="radiant-floor-heating" />} />
          <Route path="/residential/fuse-box-conversions" element={<FuseBoxBreakerConversions />} />
          <Route path="/residential/pot-lights-led-lighting" element={<PotLightsLedLighting />} />
          <Route path="/residential/ceiling-bathroom-fans-gfci" element={<CeilingBathroomFansGfci />} />
          <Route path="/residential/knob-tube-wiring" element={<KnobTubeAluminumWiring />} />
          <Route path="/residential/surge-protection-alarms" element={<SurgeProtectionSmokeAlarms />} />
          <Route path="/residential/hot-tub-pool-connections" element={<HotTubPoolAcConnections />} />
          <Route path="/residential/landscape-outdoor-lighting" element={<LandscapeOutdoorLighting />} />
          <Route path="/residential/camera-systems" element={<CameraSystems />} />
          <Route path="/residential/motion-security-lighting" element={<MotionSecurityLighting />} />
          <Route path="/residential/electrical-repair-troubleshooting" element={<ElectricalRepairTroubleshooting />} />
          <Route path="/residential-electrician" element={<ResidentialElectrician />} />
          
          {/* SEO Redirects */}
          <Route path="/services/residential/electrical-panel-upgrades" element={<Navigate to="/residential/panel-upgrades" replace />} />
          <Route path="/services/residential/electrical-panel-upgrades/" element={<Navigate to="/residential/panel-upgrades" replace />} />
          <Route path="/services/residential/landscape-outdoor-lighting" element={<Navigate to="/residential/landscape-outdoor-lighting" replace />} />
          <Route path="/services/residential/landscape-outdoor-lighting/" element={<Navigate to="/residential/landscape-outdoor-lighting" replace />} />
          <Route path="/services/residential/camera-systems" element={<Navigate to="/residential/camera-systems" replace />} />
          <Route path="/services/residential/camera-systems/" element={<Navigate to="/residential/camera-systems" replace />} />
          <Route path="/services/ev-energy/ev-charger-installation" element={<Navigate to="/ev-energy/charger-installation" replace />} />
          <Route path="/services/ev-energy/ev-charger-installation/" element={<Navigate to="/ev-energy/charger-installation" replace />} />
          <Route path="/smart-home/homeworks" element={<Navigate to="/smart-home/lutron-homeworks" replace />} />
          <Route path="/smart-home/homeworks/" element={<Navigate to="/smart-home/lutron-homeworks" replace />} />
          
          {/* Commercial Service Pages */}
          <Route path="/commercial" element={<Commercial />} />
          <Route path="/warranty" element={<Warranty />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/property-management-electrician" element={<PropertyManagement />} />
          <Route path="/industrial-electrician" element={<IndustrialElectrician />} />
          <Route path="/answers" element={<Answers />} />
          <Route path="/commercial/office-retail-restaurant" element={<OfficeRetailRestaurantWiring />} />
          <Route path="/commercial/panel-lighting-upgrades" element={<CommercialPanelLightingUpgrades />} />
          <Route path="/commercial/transformers-subpanels" element={<TransformersSubpanels />} />
          <Route path="/commercial/exit-security-lighting" element={<ExitSecurityLighting />} />
          <Route path="/commercial/power-distribution" element={<PowerDistributionMaintenance />} />
          
          {/* Industrial Service Pages */}
          <Route path="/industrial/600v-800a-services" element={<SixHundredVoltServices />} />
          <Route path="/industrial/machinery-connections" element={<MachineryConnections />} />
          <Route path="/industrial/transformer-motor-control" element={<TransformerMotorControl />} />
          <Route path="/industrial/plc-automation-wiring" element={<PlcAutomationWiring />} />
          
          {/* Smart Home Service Pages */}
          <Route path="/smart-home/lutron-homeworks" element={<LutronHomeworks />} />
          <Route path="/smart-home/radiora-2" element={<RadioRa2 />} />
          <Route path="/smart-home/radiora-3" element={<RadioRa3 />} />
          <Route path="/smart-home/palladiom-shades" element={<PalladiomShades />} />
          <Route path="/smart-home/smart-panels-occupancy" element={<SmartPanelsOccupancy />} />
          <Route path="/smart-home/voice-control" element={<VoiceControlIntegration />} />
          <Route path="/smart-home/smart-thermostats" element={<SmartThermostats />} />
          
          {/* EV & Energy Service Pages */}
          <Route path="/ev-energy/charger-installation" element={<EvChargerInstallation />} />
          <Route path="/ev-energy/generator-wiring" element={<GeneratorWiring />} />
          <Route path="/ev-energy/energy-management" element={<EnergyManagement />} />
          
          {/* Emergency Service Pages */}
          <Route path="/emergency/service" element={<EmergencyServiceRedirect />} />
          <Route path="/emergency/service/" element={<EmergencyServiceRedirect />} />
          <Route path="/emergency/electrical-repairs" element={<EmergencyElectricalRepairs />} />
          <Route path="/emergency/power-outage" element={<PowerOutageResponse />} />
          <Route path="/emergency/breaker-trips" element={<BreakerTripsFixes />} />
          <Route path="/emergency/panel-repairs" element={<EmergencyPanelRepairs />} />
          
          {/* GTA Location Pages */}
          <Route path="/gta/vaughan" element={<Vaughan />} />
          <Route path="/gta/richmond-hill" element={<RichmondHill />} />
          <Route path="/gta/markham" element={<Markham />} />
          <Route path="/gta/mississauga" element={<Mississauga />} />
          <Route path="/gta/brampton" element={<Brampton />} />
          <Route path="/gta/oakville" element={<Oakville />} />
          <Route path="/gta/north-york" element={<NorthYorkGta />} />
          <Route path="/gta/etobicoke" element={<EtobicokeGta />} />
          <Route path="/gta/scarborough" element={<ScarboroughGta />} />
          <Route path="/gta/east-york" element={<EastYorkGta />} />
          <Route path="/gta/thornhill" element={<Thornhill />} />
          <Route path="/gta/woodbridge" element={<Woodbridge />} />
          <Route path="/gta/maple" element={<Maple />} />
          <Route path="/gta/king-city" element={<KingCity />} />
          <Route path="/gta/kleinburg" element={<Kleinburg />} />
          <Route path="/gta/bolton" element={<Bolton />} />
          <Route path="/gta/caledon" element={<Caledon />} />
          <Route path="/gta/georgetown" element={<Georgetown />} />
          
          
          {/* Toronto Neighborhood Pages */}
          <Route path="/toronto/downtown-toronto" element={<DowntownToronto />} />
          {/* Duplicate neighbourhood URLs consolidated into the Areas We Serve pages */}
          <Route path="/toronto/north-york" element={<Navigate to="/gta/north-york" replace />} />
          <Route path="/toronto/north-york-toronto" element={<Navigate to="/gta/north-york" replace />} />
          <Route path="/toronto/etobicoke" element={<Navigate to="/gta/etobicoke" replace />} />
          <Route path="/toronto/etobicoke-toronto" element={<Navigate to="/gta/etobicoke" replace />} />
          <Route path="/toronto/east-york" element={<Navigate to="/gta/east-york" replace />} />
          <Route path="/toronto/east-york-toronto" element={<Navigate to="/gta/east-york" replace />} />
          <Route path="/toronto/scarborough" element={<Navigate to="/gta/scarborough" replace />} />
          <Route path="/toronto/scarborough-toronto" element={<Navigate to="/gta/scarborough" replace />} />
          <Route path="/toronto/midtown-toronto" element={<MidtownToronto />} />
          <Route path="/toronto/the-beaches" element={<TheBeaches />} />
          <Route path="/toronto/leslieville" element={<Leslieville />} />
          <Route path="/toronto/high-park" element={<HighPark />} />
          <Route path="/toronto/junction-triangle" element={<JunctionTriangle />} />
          <Route path="/toronto/forest-hill" element={<ForestHill />} />
          <Route path="/toronto/rosedale" element={<Rosedale />} />
          <Route path="/toronto/corktown" element={<Corktown />} />
          <Route path="/toronto/liberty-village" element={<LibertyVillage />} />
          <Route path="/toronto/king-west" element={<KingWest />} />
          <Route path="/toronto/yorkville" element={<Yorkville />} />
          <Route path="/toronto/riverdale" element={<Riverdale />} />
          <Route path="/toronto/the-danforth" element={<TheDanforth />} />
          <Route path="/toronto/bloor-west" element={<BloorWest />} />
          <Route path="/toronto/little-italy" element={<LittleItaly />} />
          <Route path="/toronto/the-annex" element={<TheAnnex />} />
          <Route path="/toronto/kensington-market" element={<KensingtonMarket />} />
          
          {/* Blog Routes */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/category/:slug" element={<BlogCategory />} />
          <Route path="/blog/tag/:slug" element={<BlogTag />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          
          {/* GTA Hub Route */}
          <Route path="/gta" element={<GTA />} />
          
          {/* New GTA City Routes */}
          <Route path="/gta/ajax" element={<Ajax />} />
          <Route path="/gta/aurora" element={<Aurora />} />
          <Route path="/gta/burlington" element={<Burlington />} />
          
          
          {/* Redirects for removed pages */}
          <Route path="/gta/halton-hills-georgetown" element={<Navigate to="/gta/georgetown" replace />} />
          <Route path="/gta/halton-hills" element={<Navigate to="/gta/georgetown" replace />} />
          <Route path="/gta/milton" element={<Milton />} />
          <Route path="/gta/newmarket" element={<Newmarket />} />
          <Route path="/gta/oshawa" element={<Oshawa />} />
          <Route path="/gta/pickering" element={<Pickering />} />
          <Route path="/gta/whitby" element={<Whitby />} />
          {/* Downloadable guides — noindex, not in sitemap, reached only after signup */}
          <Route path="/guides/home-electrical-checklist" element={<GuidePage slug="home-electrical-checklist" />} />
          <Route path="/guides/ev-ready-home-checklist" element={<GuidePage slug="ev-ready-home-checklist" />} />
          <Route path="/guides/lutron-lighting-planning-guide" element={<GuidePage slug="lutron-lighting-planning-guide" />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/attribution-review" element={<AttributionReview />} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
  </>
);

const App = () => (
  <BrowserRouter>
    <AppContent />
  </BrowserRouter>
);

export default App;
